import type { ReactNode } from 'react'
import { Loader2, AlertCircle, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * The spinner and a line, for a surface whose shape cannot be predicted (the
 * canvas). A list or a record has a skeleton shaped like what is coming instead.
 */
export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 px-4 py-14 text-ink-muted">
      <Loader2 className="h-4 w-4 animate-spin" />
      {label}
    </div>
  )
}

/** A quiet placeholder for a list/table body while it loads: a panel of dim rows
 * shaped like the list that's coming. */
export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="overflow-hidden rounded-lg border border-line bg-surface">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 border-b border-line px-4 py-2.5 last:border-b-0">
          <Skeleton className="h-8 w-11 shrink-0" />
          <div className="grid min-w-0 flex-1 gap-[7px]">
            <Skeleton className="h-3 w-full max-w-[18rem]" />
            <Skeleton className="h-2.5 w-full max-w-[10rem]" />
          </div>
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-5 w-16" />
        </div>
      ))}
    </div>
  )
}

/** A placeholder for a record page while it loads: a header panel over a couple
 * of content blocks, so the page doesn't pop in from a spinner. */
export function DetailSkeleton() {
  return (
    <div className="grid gap-5">
      <Skeleton className="h-3.5 w-16" />
      <div className="flex items-center gap-5 rounded-lg border border-line bg-surface p-4">
        <Skeleton className="h-[114px] w-[152px] shrink-0 rounded-md" />
        <div className="grid min-w-0 flex-1 gap-2.5">
          <Skeleton className="h-6 w-full max-w-[16rem]" />
          <Skeleton className="h-3.5 w-full max-w-[22rem]" />
        </div>
      </div>
      <div className="grid gap-2.5">
        <Skeleton className="h-3.5 w-32" />
        <Skeleton className="h-24 w-full rounded-lg" />
      </div>
    </div>
  )
}

/**
 * A surface that could not load: what failed, and "Try again". Said without
 * apology — the message is the fact and the button is the fix.
 */
export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div role="alert" className="grid justify-items-center gap-2 rounded-lg border border-line bg-surface px-6 py-9 text-center">
      <div className="mb-1 grid h-10 w-10 place-items-center rounded-md bg-danger-soft text-danger-ink">
        <AlertCircle className="h-5 w-5" />
      </div>
      <p className="m-0 max-w-[46ch] text-heading font-semibold text-ink">{message}</p>
      {onRetry ? (
        <Button variant="outline" size="sm" className="mt-1.5" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  )
}

/**
 * A list with nothing in it yet: an icon, a title, a line, and the action that
 * fixes it. On the plan grid, in a dashed frame, so it reads as a space waiting
 * to be filled. A *filtered* list that matches nothing keeps its table instead
 * (`TableEmptyRow`), so the filters stay in reach.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: LucideIcon
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="plan-grid grid justify-items-center gap-1.5 rounded-lg border border-dashed border-line-control bg-surface px-6 py-11 text-center">
      <div className="mb-1.5 grid h-11 w-11 place-items-center rounded-md bg-accent-soft text-accent-ink">
        <Icon className="h-5 w-5" />
      </div>
      <p className="m-0 text-heading font-semibold text-ink">{title}</p>
      {description ? <p className="m-0 max-w-[46ch] text-ink-muted">{description}</p> : null}
      {action ? <div className="mt-2.5">{action}</div> : null}
    </div>
  )
}
